import React, { useState } from 'react';
import { Check, Copy, Mail, MapPin, Phone } from 'lucide-react';
import { Input } from '../SubscriptionAppInput';
import { HoverTooltip } from './HoverTooltip';
export interface ContactHeaderFields {
  name: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}
interface ContactHeaderDetailsProps {
  fields: ContactHeaderFields;
  isEditing: boolean;
  onChange: (updates: Partial<ContactHeaderFields>) => void;
  // Shown beside the name, e.g. the call flag
  nameAdornment?: React.ReactNode;
  // Club and tier line under the name
  clubLabels?: string[];
  // Small line(s) under the contact details, e.g. last contacted, cellar door visits
  footnote?: string | string[];
}
type CopyKey = 'email' | 'phone' | 'address';

// Name plus one compact line of email, phone and address under it, each copyable on hover.
// In edit mode the line becomes inputs.
export function ContactHeaderDetails({
  fields,
  isEditing,
  onChange,
  nameAdornment,
  clubLabels,
  footnote
}: ContactHeaderDetailsProps) {
  const [copied, setCopied] = useState<CopyKey | null>(null);
  const cityLine = `${fields.city}, ${fields.state} ${fields.zip}`;
  const copy = async (key: CopyKey, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(current => current === key ? null : current), 2000);
  };
  const items: {
    key: CopyKey;
    icon: typeof Mail;
    text: string;
    copyText: string;
    label: string;
  }[] = [{
    key: 'email',
    icon: Mail,
    text: fields.email,
    copyText: fields.email,
    label: 'Copy email address'
  }, {
    key: 'phone',
    icon: Phone,
    text: fields.phone,
    copyText: fields.phone,
    label: 'Copy phone number'
  }, {
    key: 'address',
    icon: MapPin,
    text: `${fields.street}, ${cityLine}, ${fields.country}`,
    // Address copies as one block, ready to paste into a label or form
    copyText: [fields.street, cityLine, fields.country].join('\n'),
    label: 'Copy address'
  }];
  if (isEditing) {
    return <div className="min-w-0" data-id="element-1097" data-mp="d7421qb1h18j">
        <Input value={fields.name} onChange={e => onChange({
        name: e.target.value
      })} aria-label="Name" data-id="element-1098" data-mp="d61g7wskpozh" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2" data-id="element-1099" data-mp="mxyq907uiawm">
          <Input type="email" placeholder="Email" value={fields.email} onChange={e => onChange({
          email: e.target.value
        })} aria-label="Email" data-id="element-1100" data-mp="myqmmd99ocny" />
          <Input type="tel" placeholder="Phone" value={fields.phone} onChange={e => onChange({
          phone: e.target.value
        })} aria-label="Phone" data-id="element-1101" data-mp="3qfgvr105b5tv" />
          <Input placeholder="Street address" value={fields.street} onChange={e => onChange({
          street: e.target.value
        })} aria-label="Street address" className="md:col-span-2" data-id="element-1102" data-mp="86pyakgh0dgb" />
          <div className="grid grid-cols-3 gap-2 md:col-span-2" data-id="element-1103" data-mp="1jq2f6c3300wd">
            <Input placeholder="City" value={fields.city} onChange={e => onChange({
            city: e.target.value
          })} aria-label="City" data-id="element-1104" data-mp="1fm2ynthsx230" />
            <Input placeholder="State" value={fields.state} onChange={e => onChange({
            state: e.target.value
          })} aria-label="State" data-id="element-1105" data-mp="1s4yo03xisv4k" />
            <Input placeholder="ZIP" value={fields.zip} onChange={e => onChange({
            zip: e.target.value
          })} aria-label="ZIP" data-id="element-1106" data-mp="1d9sf9971ii7j" />
          </div>
          <Input placeholder="Country" value={fields.country} onChange={e => onChange({
          country: e.target.value
        })} aria-label="Country" data-id="element-1107" data-mp="25wd4o43ygk8g" />
        </div>
      </div>;
  }
  return <div className="min-w-0" data-id="element-1108" data-mp="1w7d6uo16vuvu">
      <div className="flex flex-wrap items-center gap-3" data-id="element-1109" data-mp="1w622on3u0l47">
        <h1 className="text-2xl font-semibold text-[#0F172A]" data-id="element-1110" data-mp="215n20ymwyvhg">{fields.name}</h1>
        {nameAdornment}
      </div>
      {clubLabels && clubLabels.length > 0 && <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-[#0F172A] mt-1" data-id="element-1111" data-mp="2ou68r9qbf07f">
          <span data-id="element-1112" data-mp="2tnimgotghmle">{clubLabels[0]}</span>
          {clubLabels.length > 1 && <HoverTooltip content={<ul className="space-y-1" data-id="element-1114" data-mp="15zh2gd0xq3d9">
                  {clubLabels.map(label => <li key={label} data-id="element-1115" data-mp="10yjka0fxd9br">{label}</li>)}
                </ul>} data-id="element-1113" data-mp="15y5vk4c70fhx">
              <span className="text-[#0EA5E9] cursor-default" data-id="element-1116" data-mp="vzq5m4owjca0">
                +{clubLabels.length - 1} more
              </span>
            </HoverTooltip>}
        </div>}
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1" data-id="element-1117" data-mp="r26jyjpvjx81">
        {items.map(item => {
        const Icon = item.icon;
        const isCopied = copied === item.key;
        return <li key={item.key} className="group flex items-center gap-1.5 text-sm text-[#64748B] min-w-0" data-id="element-1118" data-mp="1jqlscnei3hae">
              <Icon className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0" data-id="element-1119" data-mp="1ewzrdshp91uf" />
              <span className="truncate" data-id="element-1120" data-mp="19yg3vnngjhzy">{item.text}</span>
              <button type="button" onClick={() => copy(item.key, item.copyText)} aria-label={item.label} className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-[#E2E8F0] transition-opacity focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/30 focus:opacity-100 ${isCopied ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} data-id="element-1121" data-mp="27ev9hc0rrgv7">
                {isCopied ? <Check className="w-3.5 h-3.5 text-[#10B981]" data-id="element-1122" data-mp="2eyo00jr3pzx8" /> : <Copy className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-1123" data-mp="2jwxwcxokb6kf" />}
              </button>
            </li>;
      })}
      </ul>
      {(Array.isArray(footnote) ? footnote : footnote ? [footnote] : []).map(line => <p key={line} className="text-xs text-[#94A3B8] mt-1" data-id="element-1124" data-mp="2tw6mffgb411i">{line}</p>)}
    </div>;
}